import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ResponsiveConversationLayout, ResponsiveLayoutProvider } from './layouts';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { conversationService } from '../../services/conversationService';
import { useConversationStatsQuery } from './hooks/useConversationsQuery';
import { 
  Sparkles, 
  MessageCircle, 
  Zap, 
  Brain, 
  TrendingUp,
  Users,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Wifi,
  WifiOff,
  ArrowRight,
  Star,
  Activity
} from 'lucide-react';

// Shadcn/UI components
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Separator } from '../../components/ui/separator';
import { Skeleton } from '../../components/ui/skeleton';

// Import the responsive CSS
import './layouts/responsive-conversation.css';

// Production monitoring and error tracking
import { setupErrorTracking } from '../../services/errorTracking';
import { setupPerformanceMonitoring } from '../../services/performanceMonitoring';

export interface RevolutionaryConversationsPageProps {
  className?: string;
}

interface PageState {
  status: 'initializing' | 'loading' | 'ready' | 'error';
  selectedConversationId?: string;
  connectionStatus: 'connected' | 'disconnected' | 'reconnecting';
  stats?: {
    totalConversations: number;
    activeConversations: number;
    responseRate: number;
    avgResponseTime: string;
  };
}

type PageAction = 
  | { type: 'SET_STATUS'; status: PageState['status'] }
  | { type: 'SET_CONVERSATION'; conversationId: string }
  | { type: 'SET_CONNECTION'; status: PageState['connectionStatus'] }
  | { type: 'SET_STATS'; stats: PageState['stats'] };

const pageReducer = (state: PageState, action: PageAction): PageState => {
  switch (action.type) {
    case 'SET_STATUS':
      return { ...state, status: action.status };
    case 'SET_CONVERSATION':
      return { ...state, selectedConversationId: action.conversationId };
    case 'SET_CONNECTION':
      return { ...state, connectionStatus: action.status };
    case 'SET_STATS':
      return { ...state, stats: action.stats };
    default:
      return state;
  }
};

/**
 * Revolutionary Conversations Interface - Sleek Shadcn/UI Version
 * 
 * Features the most advanced conversation management interface with:
 * - Sleek shadcn/ui components throughout
 * - Perfect responsive design with no escaping elements
 * - Modern animations and micro-interactions
 * - Professional color scheme and typography
 * - Enterprise-grade performance and reliability
 */
export const RevolutionaryConversationsPage: React.FC<RevolutionaryConversationsPageProps> = ({
  className
}) => {
  const [state, dispatch] = React.useReducer(pageReducer, {
    status: 'initializing',
    connectionStatus: 'connected'
  });

  const shouldReduceMotion = useReducedMotion();
  
  // Feature flags for progressive enhancement
  const isRevolutionaryEnabled = useFeatureFlag('conversations-v2');
  const isAIEnabled = useFeatureFlag('ai-response-suggestions');
  const isIntelligenceHubEnabled = useFeatureFlag('intelligence-hub');

  // React Query for stats (demonstrating QueryClient is working)
  const { data: queryStats, isLoading: queryStatsLoading, error: queryStatsError } = useConversationStatsQuery();

  // Initialize the page with production monitoring
  useEffect(() => {
    const initializeRevolutionaryInterface = async () => {
      performance.mark('revolutionary-conversations-init-start');
      
      try {
        dispatch({ type: 'SET_STATUS', status: 'loading' });
        
        // Initialize production monitoring
        setupErrorTracking();
        setupPerformanceMonitoring();
        
        // Load initial conversation stats (React Query will handle this automatically)
        // But also set fallback stats if React Query hasn't loaded yet
        if (!queryStats && !queryStatsLoading) {
          try {
            const stats = await conversationService.getConversationStats();
            dispatch({ type: 'SET_STATS', stats });
          } catch (error) {
            console.warn('Failed to load initial stats:', error);
          }
        }

        // Simulate realistic loading time for better UX
        await new Promise(resolve => setTimeout(resolve, 600));
        
        performance.mark('revolutionary-conversations-init-end');
        performance.measure(
          'revolutionary-conversations-init',
          'revolutionary-conversations-init-start',
          'revolutionary-conversations-init-end'
        );
        
        dispatch({ type: 'SET_STATUS', status: 'ready' });
        
      } catch (error) {
        console.error('Revolutionary Conversations Interface initialization failed:', error);
        dispatch({ type: 'SET_STATUS', status: 'error' });
      }
    };

    initializeRevolutionaryInterface();
  }, [queryStats, queryStatsLoading]);

  // Sync React Query stats with local state
  useEffect(() => {
    if (queryStats && !queryStatsError) {
      dispatch({ type: 'SET_STATS', stats: queryStats });
    }
  }, [queryStats, queryStatsError]);

  // Monitor connection status with better logic
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch('/api/health', {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        dispatch({ 
          type: 'SET_CONNECTION', 
          status: response.ok ? 'connected' : 'disconnected' 
        });
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          dispatch({ type: 'SET_CONNECTION', status: 'disconnected' });
        }
      }
    };

    // Check immediately, then much less frequently to avoid rate limiting
    checkConnection();
    const interval = setInterval(checkConnection, 2 * 60 * 1000); // Every 2 minutes (instead of 30 seconds)
    
    return () => clearInterval(interval);
  }, []);

  // Handle conversation selection with smooth transitions
  const handleConversationSelect = useCallback((conversationId: string) => {
    dispatch({ type: 'SET_CONVERSATION', conversationId });
    performance.mark(`conversation-select-${conversationId}`);
  }, []);

  // Handle conversation creation
  const handleConversationCreate = useCallback(() => {
    console.log('Creating new conversation...');
    performance.mark('conversation-create');
  }, []);

  // Render loading state with sleek skeleton
  if (state.status === 'initializing' || state.status === 'loading') {
    return (
      <div className="h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center">
        <Card className="max-w-2xl w-full mx-4">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary/70 rounded-2xl flex items-center justify-center shadow-lg">
                  <MessageCircle className="w-8 h-8 text-primary-foreground" />
                </div>
                <motion.div
                  className="absolute -top-2 -right-2"
                  animate={{ 
                    rotate: [0, 360],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center shadow-md">
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                </motion.div>
              </div>
            </div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Revolutionary Conversations
            </CardTitle>
            <CardDescription className="text-lg">
              {state.status === 'initializing' 
                ? 'Setting up intelligent features...' 
                : 'Loading conversation data...'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Loading skeleton */}
              <div className="grid grid-cols-3 gap-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="text-center space-y-2">
                    <Skeleton className="h-12 w-12 rounded-xl mx-auto" />
                    <Skeleton className="h-4 w-20 mx-auto" />
                  </div>
                ))}
              </div>
              
              <Separator />
              
              {/* Progress indicators */}
              {state.stats && (
                <div className="flex items-center justify-center space-x-8">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-foreground">{state.stats.totalConversations}</div>
                    <div className="text-sm text-muted-foreground">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{state.stats.responseRate}%</div>
                    <div className="text-sm text-muted-foreground">Response Rate</div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render error state
  if (state.status === 'error') {
    return (
      <div className="h-screen bg-gradient-to-br from-background to-muted/20 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <CardTitle className="text-xl">Something went wrong</CardTitle>
            <CardDescription>
              We couldn't initialize the Revolutionary Conversations Interface. Please try again.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => window.location.reload()} 
              className="w-full"
              size="lg"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main interface with sleek design
  return (
    <ResponsiveLayoutProvider>
      <div className={`
        revolutionary-conversations-page h-screen bg-gradient-to-br from-background via-background to-muted/10
        ${className || ''}
      `}>
        {/* Sleek connection status indicator */}
        <AnimatePresence>
          {state.connectionStatus !== 'connected' && (
            <motion.div
              className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50"
              initial={{ y: -100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -100, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <Alert className={`
                max-w-sm shadow-lg border-0
                ${state.connectionStatus === 'disconnected' 
                  ? 'bg-destructive text-destructive-foreground' 
                  : 'bg-yellow-500 text-white'}
              `}>
                {state.connectionStatus === 'disconnected' ? 
                  <WifiOff className="h-4 w-4" /> : 
                  <Wifi className="h-4 w-4" />
                }
                <AlertDescription className="font-medium">
                  {state.connectionStatus === 'disconnected' ? 'Connection lost' : 'Reconnecting...'}
                </AlertDescription>
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sleek header with proper containment */}
        <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/70 rounded-xl flex items-center justify-center shadow-sm">
                    <MessageCircle className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold tracking-tight">Conversations</h1>
                    <p className="text-sm text-muted-foreground">Revolutionary AI-powered interface</p>
                  </div>
                </div>
              </div>

              {/* Real-time stats with better spacing */}
              {state.stats && (
                <div className="hidden md:flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-primary" />
                    <span className="font-semibold">{state.stats.totalConversations}</span>
                    <span className="text-sm text-muted-foreground">Total</span>
                  </div>
                  <Separator orientation="vertical" className="h-6" />
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="font-semibold">{state.stats.responseRate}%</span>
                    <span className="text-sm text-muted-foreground">Response Rate</span>
                  </div>
                  <Separator orientation="vertical" className="h-6" />
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-purple-600" />
                    <span className="font-semibold">{state.stats.avgResponseTime}</span>
                    <span className="text-sm text-muted-foreground">Avg Response</span>
                  </div>
                </div>
              )}

              {/* Feature badges */}
              <div className="flex items-center space-x-2">
                {isAIEnabled && (
                  <Badge variant="secondary" className="bg-purple-100 text-purple-700 hover:bg-purple-200">
                    <Brain className="w-3 h-3 mr-1" />
                    AI
                  </Badge>
                )}
                {isIntelligenceHubEnabled && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-200">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Intelligence
                  </Badge>
                )}
                <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                  <Activity className="w-3 h-3 text-green-500" />
                  <span>Live</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main content with proper height calculation */}
        <main className="h-[calc(100vh-5rem)] overflow-hidden">
          <ResponsiveConversationLayout
            conversationId={state.selectedConversationId}
            selectedConversationId={state.selectedConversationId}
            onConversationSelect={handleConversationSelect}
            onConversationCreate={handleConversationCreate}
            className="h-full"
          />
        </main>

        {/* Sleek development debug panel */}
        {import.meta.env.DEV && (
          <div className="fixed bottom-4 right-4 z-50">
            <Card className="w-72">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <CardTitle className="text-sm">Debug Panel</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                <div className="flex justify-between items-center">
                  <span>Revolutionary Interface:</span>
                  <Badge variant={isRevolutionaryEnabled ? "default" : "secondary"}>
                    {isRevolutionaryEnabled ? 'ON' : 'OFF'}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span>Connection:</span>
                  <Badge variant={state.connectionStatus === 'connected' ? "default" : "destructive"}>
                    {state.connectionStatus}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span>Status:</span>
                  <Badge variant="outline">{state.status}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span>React Query:</span>
                  <Badge variant={queryStats ? "default" : queryStatsLoading ? "secondary" : "destructive"}>
                    {queryStats ? 'Working' : queryStatsLoading ? 'Loading' : queryStatsError ? 'Error' : 'Ready'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </ResponsiveLayoutProvider>
  );
};

export default RevolutionaryConversationsPage;
